import { FC, useMemo, useCallback } from 'react';
import { TaskStatus } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { TaskCenterQueries } from '@frontend/api-task-center';
import { EmptyStateConfig, InfinitePaginatedList, EmptyStates } from '@frontend/components';
import { useDashboardWidget } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { TaskItem, taskWidgetTrackingIds } from '@frontend/task-center-components';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { isWidgetWidthSmall } from '../../../utils/widgets';
import { ListSkeletonLoader } from './loaders/skeleton-loaders';
import { listContainer, listBorderStyle, emptyStateListWrapper } from './styles';

interface TaskListProps {
  query: ReturnType<typeof TaskCenterQueries.useGetWidgetTasksList>;
  status: TaskStatus;
}

export const TaskList: FC<TaskListProps> = ({ query, status }) => {
  const { t } = useTranslation('task-center');
  const { isTaskCenterOpen, setIsTaskCenterOpen, setTaskMode, taskMode } = useTaskCenterStore([
    'setTaskMode',
    'setIsTaskCenterOpen',
    'isTaskCenterOpen',
    'taskMode',
  ]);

  const { currentBreakpoint, currentSize } = useDashboardWidget();

  const isWidthSmall = isWidgetWidthSmall(currentBreakpoint, currentSize);

  const onClickCreateTask = useCallback(() => {
    if (!isTaskCenterOpen) {
      setIsTaskCenterOpen(true);
    }
    if (taskMode !== 'create') {
      setTaskMode('create');
    }
  }, [isTaskCenterOpen, taskMode, setIsTaskCenterOpen, setTaskMode]);

  const emptyStateConfig: EmptyStateConfig = useMemo(() => {
    const baseConfig = {
      header: '',
      action: {
        label: 'Create Tasks',
        onClick: onClickCreateTask,
        trackingId:
          status === TaskStatus.STATUS_IN_PROGRESS
            ? taskWidgetTrackingIds.createTaskInProgress
            : taskWidgetTrackingIds.createTaskTodo,
      },
      styles: emptyStateListWrapper,
    };

    if (status === TaskStatus.STATUS_IN_PROGRESS) {
      return {
        ...baseConfig,
        type: 'widgetTaskInProgress',
        description: t('No tasks in progress. Once you start working on a task, move it here to track your progress.'),
        header: '',
      };
    }
    return {
      ...baseConfig,
      type: 'taskTodo',
      description: t('No pending tasks! Any assigned tasks that need your attention will appear here'),
    };
  }, [onClickCreateTask, status, t]);

  return (
    <div css={[listContainer, !isWidthSmall && listBorderStyle]}>
      <InfinitePaginatedList
        infiniteQueryProps={query}
        emptyStateConfig={emptyStateConfig}
        renderListItem={({ listItem }) => <TaskItem trackingIds={taskWidgetTrackingIds} isFromWidget {...listItem} />}
        height='100%'
        skeletonLoader={<ListSkeletonLoader />}
        errorState={
          <EmptyStates
            emptyStateConfig={{
              type: 'widgetTaskApiError',
              header: '',
              description: t(`We couldn’t connect to your tasks. Something went wrong while connecting to the server`),
              styles: emptyStateListWrapper,
              action: {
                label: t('Retry'),
                onClick: () => query.refetch(),
                trackingId: taskWidgetTrackingIds.retryApiCall,
              },
            }}
          />
        }
      />
    </div>
  );
};
